import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import { formatShabbatTimeRule } from '../../shabbatZmanUtils';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const Modern = ({ shabbat, processedCustomTimes, drashaText, loading, error, title = 'זמני שבת', showTitle = true, entryIcon = 'Flame', exitIcon = 'Moon', dayEntryTime, dayExitTime, showTimeRule = false, showEntryExit = true, shabbatLeyning, showLeyning, showHaftarah, leyningDisplayMode }) => {
    const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Flame'];
    const ExitIcon = WIDGET_ICON_MAP[exitIcon] || WIDGET_ICON_MAP['Moon'];
    if (loading) {
        return (
            <div style={styles.stateContainer}>
                <div style={styles.loading}>טוען זמני שבת...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.stateContainer}>
                <div style={styles.error}>{error}</div>
            </div>
        );
    }

    if (!shabbat) {
        return (
            <div style={styles.stateContainer}>
                <div style={styles.loading}>אין נתוני שבת</div>
            </div>
        );
    }

    const parasha = shabbat.meta?.parsha || shabbat.parsha || 'בהר - בחוקותי';
    const candleLighting = dayEntryTime || shabbat.meta?.candleLighting || shabbat.friday?.candleLighting || shabbat.candleLighting || '--:--';
    const havdalah = dayExitTime || shabbat.meta?.havdalah || shabbat.shabbat?.havdalah || shabbat.havdalah || '--:--';

    return (
        <div
            style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                direction: 'rtl',
                padding: '20px',
                boxSizing: 'border-box',
            }}
            role="region"
            aria-label="זמני שבת"
        >
            {/* Header */}
            {showTitle && (
                <div style={{
                    padding: '20px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e5ddd3'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <EntryIcon style={{ width: '24px', height: '24px', color: '#8a6d08' }} />
                        <h2 style={{ fontWeight: 'bold', fontSize: '24px', color: '#1e3a5f', margin: 0 }}>
                            {title}
                        </h2>
                    </div>
                </div>
            )}

            {/* Parasha Title */}
            <div style={{ padding: '20px 16px', textAlign: 'center' }}>
                <h3 style={{ fontSize: '48px', fontWeight: '800', color: '#1e3a5f', margin: 0 }}>
                    {shabbat.meta?.parshaShowPrefix !== false ? 'פרשת ' : ''}{parasha}
                </h3>
                {shabbat.meta?.parshaSubtitle && (
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#1e3a5f', opacity: 0.75, marginTop: '6px' }}>
                        {shabbat.meta.parshaSubtitle}
                    </div>
                )}
            </div>

            {/* Leyning Display */}
            {(showLeyning || showHaftarah) && shabbatLeyning && (
                <div style={{
                    padding: '12px 16px',
                    marginBottom: '12px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid #e5ddd3',
                }}>
                    <LeyningDisplay
                        leyning={shabbatLeyning}
                        mode={leyningDisplayMode}
                        showHaftarah={showHaftarah}
                        showMaftir={leyningDisplayMode === 'full'}
                        compact={false}
                    />
                </div>
            )}

            {/* Main Times - Large & Accessible with borders */}
            <div style={{ padding: '0 16px 16px' }}>
                {showEntryExit && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                    marginBottom: '16px',
                    minHeight: 'auto'
                }}>
                    {/* Candle Lighting */}
                    <div
                        style={{
                            padding: '20px 16px',
                            borderRadius: '12px',
                            textAlign: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            border: '2px solid #8a6d08',
                            minHeight: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                        aria-label={`כניסת שבת בשעה ${candleLighting}`}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#8a6d08'
                            }}>
                                <EntryIcon style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', color: '#64748b' }}>
                            כניסת שבת
                        </div>
                        <div style={{
                            fontSize: '48px',
                            fontWeight: '800',
                            letterSpacing: '0.05em',
                            color: '#1e3a5f',
                            lineHeight: '1'
                        }}>
                            {candleLighting}
                        </div>
                    </div>

                    {/* Havdalah */}
                    <div
                        style={{
                            padding: '20px 16px',
                            borderRadius: '12px',
                            textAlign: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            border: '2px solid #1e3a5f',
                            minHeight: '140px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                        aria-label={`צאת שבת בשעה ${havdalah}`}
                    >
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#1e3a5f'
                            }}>
                                <ExitIcon style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                        </div>
                        <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', color: '#64748b' }}>
                            צאת שבת
                        </div>
                        <div style={{
                            fontSize: '48px',
                            fontWeight: '800',
                            letterSpacing: '0.05em',
                            color: '#1e3a5f',
                            lineHeight: '1'
                        }}>
                            {havdalah}
                        </div>
                    </div>
                </div>
                )}

                {/* דרשת ליל שבת */}
                {drashaText && (
                    <div style={{
                        marginBottom: '16px',
                        textAlign: 'center',
                        padding: '10px 45px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        border: '2px solid rgb(30, 58, 95)',
                    }}>
                        <div style={{
                            fontSize: '24px',
                            color: '#1e3a5f',
                            fontWeight: 'bold',
                            whiteSpace: 'pre-wrap',
                        }}>
                            {drashaText}
                        </div>
                    </div>
                )}

                {/* Custom Times - All user-defined times, sorted by day then time */}
                {processedCustomTimes && processedCustomTimes.length > 0 && (
                    <div style={{
                        borderRadius: '12px',
                        padding: '16px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid #e5ddd3'
                    }}>
                        {/* זמני יום שישי */}
                        {processedCustomTimes
                            .filter(ct => (ct.day || 'friday') === 'friday')
                            .sort((a, b) => {
                                const timeToMinutes = (timeStr) => {
                                    if (!timeStr || timeStr === '--:--') return -1;
                                    const [hours, minutes] = timeStr.split(':').map(Number);
                                    return hours * 60 + minutes;
                                };
                                return timeToMinutes(a.time) - timeToMinutes(b.time);
                            })
                            .map((ct, index, arr) => (
                                <div
                                    key={`friday-${index}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingTop: index > 0 ? '8px' : '0',
                                        paddingBottom: '8px',
                                        marginBottom: '8px',
                                        borderBottom: '1px solid #f0ebe3'
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ fontSize: '22px', fontWeight: '700', color: '#1f3a5f' }}>
                                            {ct.label}
                                        </span>
                                        {showTimeRule && formatShabbatTimeRule(ct) && (
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400, lineHeight: 1 }}>
                                                {formatShabbatTimeRule(ct)}
                                            </span>
                                        )}
                                    </div>
                                    <span style={{
                                        fontSize: '28px',
                                        fontWeight: '700',
                                        padding: '4px 12px',
                                        borderRadius: '8px',
                                        color: '#1e3a5f',
                                        backgroundColor: '#f5f0e8'
                                    }}>
                                        {ct.time}
                                    </span>
                                </div>
                            ))}

                        {/* זמני שבת */}
                        {processedCustomTimes
                            .filter(ct => (ct.day || 'friday') === 'shabbat')
                            .sort((a, b) => {
                                const timeToMinutes = (timeStr) => {
                                    if (!timeStr || timeStr === '--:--') return -1;
                                    const [hours, minutes] = timeStr.split(':').map(Number);
                                    return hours * 60 + minutes;
                                };
                                return timeToMinutes(a.time) - timeToMinutes(b.time);
                            })
                            .map((ct, index, arr) => (
                                <div
                                    key={`shabbat-${index}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingTop: index > 0 ? '8px' : '0',
                                        paddingBottom: index < arr.length - 1 ? '8px' : '0',
                                        marginBottom: index < arr.length - 1 ? '8px' : '0',
                                        borderBottom: index < arr.length - 1 ? '1px solid #f0ebe3' : 'none'
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ fontSize: '22px', fontWeight: '700', color: '#1f3a5f' }}>
                                            {ct.label}
                                        </span>
                                        {showTimeRule && formatShabbatTimeRule(ct) && (
                                            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400, lineHeight: 1 }}>
                                                {formatShabbatTimeRule(ct)}
                                            </span>
                                        )}
                                    </div>
                                    <span style={{
                                        fontSize: '28px',
                                        fontWeight: '700',
                                        padding: '4px 12px',
                                        borderRadius: '8px',
                                        color: '#1e3a5f',
                                        backgroundColor: '#f5f0e8'
                                    }}>
                                        {ct.time}
                                    </span>
                                </div>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    stateContainer: {
        width: '100%',
        padding: '20px',
        textAlign: 'center',
        direction: 'rtl'
    },
    loading: { fontSize: '20px', color: '#1e3a5f' },
    error: { fontSize: '18px', color: '#D32F2F', marginBottom: '10px' }
};

export default Modern;
